import { Injectable, Injector } from '@angular/core';
import * as TrimbleMaps from '@trimblemaps/trimblemaps-js';
import MapMenus from '@trimblemaps/trimblemaps-mapmenus';
import ExportMap from '@trimblemaps/trimblemaps-exportmap';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { MapHandlerManager } from '../utils/mapHandlerManager';
import { MapStyle, MapRegion } from '../_models/trimblemaps';
import { mapLocationOption } from '../_models/mapLocationOption';
import { environment } from '../../environments/environment';
import { UserService } from 'src/app/services/user.service';
import { SubscriptionLevel } from '../auth/models/user';
import { BrazeService } from './braze.service';
import { GoogleAnalytics } from './google-analytics.service';
import RouteModifiers from '@trimblemaps/trimblemaps-routemodifiers';
import { RouteService } from './route.service';
import { RouteState } from '../_models/state/routeState';

interface MapStore {
  currentStyle: MapStyle;
  mapLocation: mapLocationOption;
}

const INITIAL_STATE: MapStore = {
  currentStyle: TrimbleMaps.Common.Style.TRANSPORTATION,
  mapLocation: { zoom: 0, center: [0, 0] },
};

@Injectable()
export class MapService {
  geolocateControl: TrimbleMaps.GeolocateControl;
  routeModifiersControl: any;
  styleChange: boolean;
  mapLoaded: boolean;
  constructor(
    private injector: Injector,
    private brazeService: BrazeService,
    private userService: UserService,
    private googleAnalytics: GoogleAnalytics
  ) {}
  map: TrimbleMaps.Map;
  exportMapControl: ExportMap;
  private isLocationAllowed = false;

  mapHandlerManager = new MapHandlerManager();

  private store: BehaviorSubject<MapStore> = new BehaviorSubject<MapStore>({
    ...INITIAL_STATE,
  });

  readonly store$: Observable<MapStore> = this.store.asObservable();

  // shortcut to value
  private get storeValue(): MapStore {
    return this.store.getValue();
  }

  // Observables for managed state
  readonly currentStyle$: Observable<MapStyle> = this.store$.pipe(
    map((s) => s.currentStyle),
    distinctUntilChanged()
  );

  readonly mapLocation$: Observable<mapLocationOption> = this.store$.pipe(
    map((s) => s.mapLocation),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr))
  );

  private isLoading = new BehaviorSubject<boolean>(true);
  readonly isLoading$ = this.isLoading.asObservable();

  private mapSubject = new BehaviorSubject<TrimbleMaps.Map | null>(null);
  readonly map$ = this.mapSubject.asObservable();

  private fullScreenView = new BehaviorSubject<boolean>(false);
  readonly fullScreenView$ = this.fullScreenView.asObservable();
  // TODO fill out events as needed
  // Events
  private mapLoad = new Subject<TrimbleMaps.Map>();
  readonly mapLoad$ = this.mapLoad.asObservable();

  private mapDblClick = new Subject<TrimbleMaps.MapMouseEvent>();
  readonly mapDblClick$ = this.mapDblClick.asObservable();

  private mapClick = new Subject<TrimbleMaps.MapMouseEvent>();
  readonly mapClick$ = this.mapClick.asObservable();

  private mouseMove = new Subject<TrimbleMaps.MapMouseEvent>();
  readonly mouseMove$ = this.mouseMove.asObservable();

  private mapMove = new Subject<mapLocationOption>();
  readonly mapMove$ = this.mapMove.asObservable();

  private updateStore(newStore: Partial<MapStore>) {
    this.store.next({
      ...this.storeValue,
      ...newStore,
    });
  }
  initMap(options: TrimbleMaps.MapOptions): TrimbleMaps.Map {
    this.isLoading.next(true);
    // get API Key from loggedIn user and set to map
    const userConfig = this.userService.getUserCfg();
    const userAPIKey: string = this.userService.getUserApiKey();
    TrimbleMaps.setAPIKey(environment.apiKey);

    TrimbleMaps.setAPIToken(`token=${this.userService.getUser()?.authToken}`);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    TrimbleMaps._setServiceUrls({
      BASE_URL: environment.tileswsUrl,
      RAIL_URL: environment.railwsUrl,
      REST_URL: environment.pcmwsUrl,
      SINGLESEARCH_URL: environment.singleSearchbaseUrl,
    });
    options.style = userConfig.mapStyle;

    if (options.style) {
      this.updateStore({
        currentStyle: options.style,
        mapLocation: {
          zoom: options.zoom,
          center: options.center,
          source: 'LOAD',
        },
      });
    }

    this.map = new TrimbleMaps.Map(options);
    const mapMenusControl = new MapMenus({
      showBaseStyles: true,
      showCustomStyles: false,
      showContentLayers:
        this.userService.getUser()?.subscriptionLevel ===
        SubscriptionLevel.PREMIUM,
    });
    const addonServices = {
      STYLE_URL: `${environment.tileswsUrl}/styles/`,
    };
    // example, STYLE_URL: "https://tiles.alk.com/styles/"
    mapMenusControl._setServiceUrls(addonServices);
    this.map.addControl(mapMenusControl);
    this.geolocateControl = new TrimbleMaps.GeolocateControl({
      trackUserLocation: false,
      showUserLocation: false,
      showAccuracyCircle: false,
    });
    this.map.addControl(this.geolocateControl);
    userConfig.afSetIDs = userConfig?.afSetIDs ? userConfig.afSetIDs : [-1];
    if (!userConfig?.afSetIDs) {
      this.userService.updateUserCfg(userConfig);
    }
    if (!userConfig?.selectedPage ?? false) {
      this.userService.updateUserCfg({ selectedPage: 1 });
    }
    this.routeModifiersControl = new RouteModifiers({
      selectedIds: userConfig?.afSetIDs[0] !== -1 ? userConfig.afSetIDs : [], //need to pass from userConfig to retain selection
      selectedPage: userConfig?.selectedPage ? userConfig.selectedPage : 1, //need to pass from userConfig to retain selection
      showAll: userConfig?.afSetIDs?.length > 0, //need to pass from userConfig to retain selection
    });
    const routeModifierServices = {
      STYLE_URL: `${environment.tileswsUrl}/styles/`,
      BASE_URL: `${environment.tileswsUrl}/api/VectorAvoidsFavors/afsets/`,
    };

    this.routeModifiersControl._setServiceUrls(routeModifierServices);

    this.map.addControl(this.routeModifiersControl);

    this.map.addControl(
      new TrimbleMaps.ScaleControl({
        unit:
          options?.region === TrimbleMaps.Common.Region.NA
            ? 'imperial'
            : 'metric',
      }),
      'bottom-left'
    );

    this.exportMapControl = new ExportMap({
      pageTitle: 'PC*MILER Web',
      top: 0,
      left: 0,
      addButton: false,
      attributionPosition: 'bottom-left',
    });
    this.map.addControl(this.exportMapControl);

    this.map.addControl(
      new TrimbleMaps.WeatherAlertFilterControl({
        severity: 'All',
        urgency: 'All',
        certainty: 'All',
        isVisible: false,
      }),
      'bottom-right'
    );

    this.map.addControl(
      new TrimbleMaps.WeatherAlertLegendControl(),
      'bottom-left'
    );

    this.mapHandlerManager.setMap(this.map);

    // Do not use the .on() method of the map, use the mapHandlerManager instead.
    // The mapHandlerManager will hold all the listeners and unsubscribe via the remove/removeAll.
    this.mapHandlerManager.add({
      eventName: 'load',
      listener: (evt: TrimbleMaps.MapEvent) => {
        const fullscreenControl = new TrimbleMaps.FullscreenControl({
          container: document.querySelector('body') as HTMLElement,
        });
        this.map.addControl(fullscreenControl, 'top-right');
        this.mapLoaded = true;
        this.mapLoad.next(evt.target);
        this.isLoading.next(false);
      },
    });

    document.addEventListener('fullscreenchange', (e) => {
      if (document.fullscreenElement) {
        this.fullScreenView.next(true);
      } else {
        this.fullScreenView.next(false);
      }
    });

    this.mapHandlerManager.add({
      eventName: 'dblclick',
      listener: (evt: TrimbleMaps.MapMouseEvent) => {
        this.mapDblClick.next(evt);
      },
    });
    this.mapHandlerManager.add({
      eventName: 'click',
      listener: (evt: TrimbleMaps.MapMouseEvent) => {
        this.mapClick.next(evt);
      },
    });
    this.mapHandlerManager.add({
      eventName: 'mousemove',
      listener: (evt: TrimbleMaps.MapMouseEvent) => {
        this.mouseMove.next(evt);
      },
    });
    this.mapHandlerManager.add({
      eventName: 'moveend',
      listener: (evt: TrimbleMaps.MapEvent) => {
        this.updateStore({
          mapLocation: {
            zoom: evt.target.getZoom(),
            center: [
              parseFloat(evt.target.getCenter().lng.toFixed(6)),
              parseFloat(evt.target.getCenter().lat.toFixed(6)),
            ],
            source: evt['source'] ? evt['source'] : 'DEFAULT',
            pitch: evt.target.getPitch(),
            bearing: evt.target.getBearing(),
          },
        });
      },
    });
    this.mapHandlerManager.add({
      eventName: 'style.load',
      listener: (evt: TrimbleMaps.MapEvent) => {
        // _styleName is not part of the public TS types for customer use, but we use
        // it frequently for internal purposes and it's stable enough to use here.

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const style = evt.target._styleName;

        if (this.mapLoaded) {
          this.styleChange = true;
        }
        this.updateStore({ currentStyle: style });
        this.userService.updateUserCfg({ mapStyle: style });
      },
    });
    this.mapHandlerManager.add({
      eventName: 'content.visibility',
      listener: (evt: TrimbleMaps.ContentVisibilityEvent) => {
        this.setLayerDetailsUserConfig(evt);
        if (
          evt.id === 'weatherradar' ||
          evt.id === 'weathercloud' ||
          evt.id === 'weatheralert' ||
          evt.id === 'weather_road_surface'
        ) {
          if (evt.isVisible) {
            this.brazeService.track('Show Weather Layers');
            this.googleAnalytics.addGA(
              `Show_${evt.id}_Layer`,
              'Weather_Layers_Click',
              'Content Layers',
              'Show Weather Layers'
            );
          } else {
            this.brazeService.track('Hide Weather Layers');
            this.googleAnalytics.addGA(
              `Hide_${evt.id}_Layer`,
              'Weather_Layers_Click',
              'Content Layers',
              'Hide Weather Layers'
            );
          }
        }
        if (
          evt.id === 'traffic' ||
          evt.id === 'trafficincidents' ||
          evt.id === 'trafficcameras'
        ) {
          if (evt.isVisible) {
            this.brazeService.track('Show Traffic');
            this.googleAnalytics.addGA(
              `Show_${evt.id}`,
              'Traffic_Layers_Click',
              'Content Layers',
              'Show Trafiic'
            );
          } else {
            this.brazeService.track('Hide Traffic');
            this.googleAnalytics.addGA(
              `Hide_${evt.id}`,
              'Traffic_Layers_Click',
              'Content Layers',
              'Hide Trafiic'
            );
          }
        }
      },
    });
    this.geolocateControl.on('geolocate', (evt) => {
      const routes = this.injector.get(RouteService);
      let selectedRoute: RouteState;
      routes.store$.subscribe((routeState) => {
        selectedRoute = routeState?.routeList.find(
          (route) => routeState?.selectedRoute?.id === route?.id
        );
      });
      if (selectedRoute?.stopsState?.length === 1) {
        this.setMapCenter(selectedRoute?.stopsState[0].lngLat);
      }
      if (selectedRoute?.stopsState?.length > 1) {
        routes.frameRouteOnMap(selectedRoute?.id);
      }
    });
    this.map.once('load', (evt) => {
      this.setLayerDetailsToMap(userConfig, mapMenusControl);
      if (!this.isLocationAllowed) {
        this.geolocateControl.trigger();
      }
      this.isLocationAllowed = true;
      this.mapSubject.next(evt.target);
    });
    this.map.on('modifiersetsloaded', (evt) => {
      //when Route Modifiers control panel gets loaded.
      const totalAFSetIds = this.routeModifiersControl?._afSetIds;
      const page = this.routeModifiersControl.getPage();
      this.userService.setuserAFSets(totalAFSetIds ? totalAFSetIds : []);
      const selectedAFSetIds = evt?.setIds ? evt?.setIds : [];
      this.userService.updateAfSetBySelection(
        selectedAFSetIds,
        page?.selectedPage
      );
    });

    this.map.on('allmodifiersvisibilitychange', (evt) => {
      // MAY BE TIMEOUT REQUIRED
      const AFSets = this.routeModifiersControl.getAFSets();
      const page = this.routeModifiersControl.getPage();
      if (evt.isSelected) {
        AFSets.afSetIDs = [-1];
        if (this.routeModifiersControl) {
          const currUserConfig = this.userService.getUserCfg();
          this.routeModifiersControl._afSetIds = currUserConfig?.allSetIds;
          this.routeModifiersControl._selectedAFSets =
            currUserConfig?.afSetIDs[0] !== -1
              ? currUserConfig.afSetIDs
              : currUserConfig?.allSetIds;
          this.routeModifiersControl._options.showAll = true;
          this.routeModifiersControl.setAFSets(
            currUserConfig?.allSetIds ? currUserConfig.allSetIds : -1
          );
        }
      }
      this.userService.updateAfSetBySelection(
        AFSets.afSetIDs ? AFSets.afSetIDs : [],
        page?.selectedPage
      );
      //let selectedAfSetFromService: [] = this.userService.getSelectedAFSets();
    });
    this.map.on('modifiervisibilitychange', (evt) => {
      // MAY BE TIMEOUT REQUIRED
      const AFSets = this.routeModifiersControl.getAFSets();
      const page = this.routeModifiersControl.getPage();

      this.userService.updateAfSetBySelection(
        AFSets.afSetIDs ? AFSets.afSetIDs : [],
        page?.selectedPage
      );
      //let selectedAfSetFromService: [] = this.userService.getSelectedAFSets();
    });
    this.map.on('allmodifiersloaded', (evt) => {
      const allsetIds = evt.setIds;
      this.userService.updateUserCfg({ allSetIds: allsetIds });
    });
    this.map.on('routemodifiers', (evt) => {
      if (this.routeModifiersControl && this.styleChange) {
        const currUserConfig = this.userService.getUserCfg();
        this.routeModifiersControl._options.selectedIds =
          currUserConfig?.afSetIDs[0] !== -1
            ? currUserConfig.afSetIDs
            : currUserConfig?.allSetIds;
        this.routeModifiersControl._options.selectedPage =
          currUserConfig?.selectedPage ? currUserConfig.selectedPage : 1;
        this.routeModifiersControl._options.showAll =
          currUserConfig?.afSetIDs?.length > 0;
        this.routeModifiersControl._afSetIds = currUserConfig?.allSetIds;
        this.routeModifiersControl._selectedAFSets =
          currUserConfig?.afSetIDs[0] !== -1
            ? currUserConfig.afSetIDs
            : currUserConfig?.allSetIds;
        this.routeModifiersControl[
          '_routeModifierLayerInstance'
        ].setSelectedSets(this.routeModifiersControl._options.selectedIds);
        this.styleChange = false;
      }
    });

    return this.map;
  }

  destroyMap() {
    if (this.map) {
      // Clear all remaining listeners
      this.mapHandlerManager.removeAll();

      this.map.remove();
    }

    this.map = null;
    this.mapSubject.next(null);
  }
  clearMaplocation() {
    this.updateStore({
      mapLocation: INITIAL_STATE.mapLocation,
    });
  }

  setApiKey(apiKey: string): void {
    TrimbleMaps.setAPIKey(apiKey);
  }

  zoomTo(zoom: number) {
    this.map.zoomTo(zoom);
  }

  zoomIn(
    options?: TrimbleMaps.AnimationOptions,
    eventData?: TrimbleMaps.EventData
  ) {
    this.map.zoomIn(options, eventData);
  }

  zoomOut(
    options?: TrimbleMaps.AnimationOptions,
    eventData?: TrimbleMaps.EventData
  ) {
    this.map.zoomOut(options, eventData);
  }

  setRegion(region: MapRegion): void {
    this.map.setRegion(region);
  }

  getRegion(): string {
    return this.map.getRegion();
  }

  getMap(): TrimbleMaps.Map {
    return this.map;
  }

  setMapCenter(lngLat: TrimbleMaps.LngLatLike, panTo?: boolean): void {
    if (panTo) {
      this.map.panTo(lngLat);
    } else {
      this.map.setCenter(lngLat);
    }
  }

  getMapCenter(): any {
    return this.map.getCenter();
  }

  flyTo(options: TrimbleMaps.FlyToOptions, evt): void {
    this.map.flyTo(options, evt);
  }

  addLayer(layer: TrimbleMaps.AnyLayer): void {
    this.map.addLayer(layer);
  }

  removeLayer(layerId: string): void {
    this.map.removeLayer(layerId);
  }

  getStyleLayers() {
    return this.map.getStyle().layers;
  }

  resize() {
    this.map?.resize();
  }

  getLatestMapLocation(): mapLocationOption {
    if (this.storeValue.mapLocation?.zoom) {
      return this.storeValue.mapLocation;
    }
    return null;
  }

  printMap() {
    this.exportMapControl.export();
  }
  setLayerDetailsToMap(userCfg, mapMenus) {
    const layers = userCfg.mapLayers;
    if (layers.Traffic) {
      this.setToMapMenuControl('Traffic Congestion', mapMenus);
    }
    if (layers.TrafficIncident) {
      this.setToMapMenuControl('Traffic Incidents', mapMenus);
    }
    if (layers.TrafficCamera) {
      this.setToMapMenuControl('Traffic Cameras', mapMenus);
    }
    if (layers.WeatherRadar) {
      this.setToMapMenuControl('Weather Radar', mapMenus);
    }
    if (layers.WeatherCloud) {
      this.setToMapMenuControl('Weather Clouds', mapMenus);
    }
    if (layers.WeatherAlert) {
      this.setToMapMenuControl('Weather Alerts', mapMenus);
    }
    if (layers.RoadSurface) {
      this.setToMapMenuControl('Weather Road Surface', mapMenus);
    }
    if (layers.TruckRestriction) {
      this.setToMapMenuControl('Truck Restrictions', mapMenus);
    }
    if (layers.Buildings3D) {
      this.setToMapMenuControl('3D Buildings', mapMenus);
    }
    if (layers.PointsOfInterest) {
      this.setToMapMenuControl('Points of Interest', mapMenus);
    }
    if (layers.Places) {
      this.setToMapMenuControl('Places', mapMenus);
    } else if (!layers.Places) {
      this.removeFromMapMenuControl('Places', mapMenus); //removed layer visiblity becuase places is always visible on map intial loads.
    }
  }
  setToMapMenuControl(layer, mapMenus) {
    mapMenus.setContentLayerVisibility(layer, true);
  }
  removeFromMapMenuControl(layer, mapMenus) {
    mapMenus.setContentLayerVisibility(layer, false);
  }
  setLayerDetailsUserConfig(evt: TrimbleMaps.ContentVisibilityEvent) {
    const userCfg = this.userService.getUserCfg();
    const layers = userCfg.mapLayers;
    if (evt?.id === 'traffic') {
      layers.Traffic = evt?.isVisible;
    } else if (evt?.id === 'trafficincidents') {
      layers.TrafficIncident = evt?.isVisible;
    } else if (evt?.id === 'trafficcameras') {
      layers.TrafficCamera = evt?.isVisible;
    } else if (evt?.id === 'weatherradar') {
      layers.WeatherRadar = evt?.isVisible;
    } else if (evt?.id === 'weathercloud') {
      layers.WeatherCloud = evt?.isVisible;
    } else if (evt?.id === 'weatheralert') {
      layers.WeatherAlert = evt?.isVisible;
    } else if (evt?.id === 'weather_road_surface') {
      layers.RoadSurface = evt?.isVisible;
    } else if (evt?.id === 'truckrestrictions') {
      layers.TruckRestriction = evt?.isVisible;
    } else if (evt?.id === '3dBuildings') {
      layers.Buildings3D = evt?.isVisible;
    } else if (evt?.id === 'pointsofinterest') {
      layers.PointsOfInterest = evt?.isVisible;
    } else if (evt?.id === 'places') {
      layers.Places = evt?.isVisible;
    }
    this.userService.updateUserCfg({ mapLayers: layers });
  }
}
